import React, { useState, useEffect, useCallback } from "react";
import {
  Container, Card, Table, Form, Row, Col,
  Spinner, Alert, Badge,
} from "react-bootstrap";
import api, { BASE_URL } from "../../services/api";
import "./Dashboard.css";

const jenisPelatihanLabel = {
  PPH01: "Penyelia Halal UMK Self Declare",
  PPH02: "Penyelia Halal UMK Reguler",
  PPH03: "Penyelia Halal Reguler",
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("id-ID", {
    day: "2-digit", month: "short", year: "numeric",
  });
};

const PesertaDaftarSertifikatTerbit = () => {
  const [data, setData]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");

  const [jenisList, setJenisList]         = useState([]);
  const [angkatanList, setAngkatanList]   = useState([]);
  const [filterJenis, setFilterJenis]     = useState("");
  const [filterAngkatan, setFilterAngkatan] = useState("");
  const [searchText, setSearchText]       = useState("");

  useEffect(() => { fetchFilterOptions(); }, []);
  useEffect(() => { fetchData(); }, [filterJenis, filterAngkatan]); // eslint-disable-line

  const fetchFilterOptions = async () => {
    try {
      const res = await api.get("/pendaftaran-penyelia/filter-options");
      if (res.data.success) {
        setJenisList(res.data.jenis_pelatihan || []);
        setAngkatanList(res.data.angkatan || []);
      }
    } catch (err) {
      console.error("Error fetching filter options:", err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const params = {};
      if (filterJenis)    params.jenis_pelatihan = filterJenis;
      if (filterAngkatan) params.angkatan        = filterAngkatan;
      const res = await api.get("/pendaftaran-penyelia/daftar-sertifikat-terbit", { params });
      if (res.data.success) setData(res.data.data);
      else setError("Gagal memuat data.");
    } catch (err) {
      setError(err.response?.data?.message || "Gagal memuat data dari server.");
    } finally {
      setLoading(false);
    }
  }, [filterJenis, filterAngkatan]);

  const filteredData = data.filter((row) => {
    if (!searchText) return true;
    const term = searchText.toLowerCase();
    return (
      row.nama?.toLowerCase().includes(term) ||
      row.email?.toLowerCase().includes(term) ||
      row.no_hp?.toLowerCase().includes(term) ||
      row.nik?.toLowerCase().includes(term) ||
      row.no_register?.toLowerCase().includes(term) ||
      row.no_sertifikat?.toLowerCase().includes(term)
    );
  });

  return (
    <Container fluid>
      <div className="dashboard-header mb-4">
        <h2>
          <i className="fas fa-file-certificate me-2"></i>
          Daftar Sertifikat Terbit
        </h2>
        <p className="text-muted mb-0">
          Peserta <Badge bg="success">LULUS</Badge> yang sertifikatnya sudah digenerate
        </p>
      </div>

      {/* Filter */}
      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Jenis Pelatihan</Form.Label>
              <Form.Select
                value={filterJenis}
                onChange={(e) => { setFilterJenis(e.target.value); setFilterAngkatan(""); }}
              >
                <option value="">— Semua —</option>
                {jenisList.map((j) => (
                  <option key={j} value={j}>{jenisPelatihanLabel[j] || j}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label className="fw-semibold mb-1">Angkatan</Form.Label>
              <Form.Select
                value={filterAngkatan}
                onChange={(e) => setFilterAngkatan(e.target.value)}
              >
                <option value="">— Semua —</option>
                {angkatanList.map((a) => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold mb-1">Cari</Form.Label>
              <Form.Control
                type="text"
                placeholder="Nama, email, NIK, no. register, no. sertifikat..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col md={2} className="text-end">
              <span className="text-muted small">{filteredData.length} data</span>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {error && <Alert variant="danger" className="m-3">{error}</Alert>}
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Memuat data...</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">#</th>
                  <th>No. Register</th>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>Jenis Pelatihan</th>
                  <th>Angkatan</th>
                  <th>No. Sertifikat</th>
                  <th>Tgl. Terbit</th>
                  <th className="text-center">Unduh</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center text-muted py-5">
                      <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                      Belum ada sertifikat yang diterbitkan
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row, idx) => (
                    <tr key={row.email}>
                      <td className="ps-3">{idx + 1}</td>
                      <td>{row.no_register || "-"}</td>
                      <td className="fw-semibold">{row.nama || "-"}</td>
                      <td>{row.email}</td>
                      <td>{jenisPelatihanLabel[row.jenis_pelatihan] || row.jenis_pelatihan || "-"}</td>
                      <td>{row.angkatan || "-"}</td>
                      <td><small>{row.no_sertifikat || "-"}</small></td>
                      <td>{formatDate(row.tgl_terbit)}</td>
                      <td className="text-center">
                        <a
                          href={`${BASE_URL}/uploads/sertifikat/${row.file_sertifikat}`}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-sm btn-outline-success"
                        >
                          <i className="fas fa-download me-1"></i>PDF
                        </a>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PesertaDaftarSertifikatTerbit;

